import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {NavLink, withRouter} from 'react-router-dom';
import logo from '../../../../assets/images/MA_Logo_New_2';
import homeIcon from '../../../../assets/images/homeIcon';
import projectIcon from '../../../../assets/images/Foldericon';
import invoiceIcon from '../../../../assets/images/invoiceIcon';
import referralIcon from '../../../../assets/images/referralsIcon';
import photoIcon from '../../../../assets/images/photoIcon';
import settingsIcon from '../../../../assets/images/settingsIcon';
import supportIcon from '../../../../assets/images/supportIcon';
import UserAvatar from '../photographers/photograher_avatar';
import '../../../stylesheets/app_navigation.scss';

// Match the existing desktop role precedence, with an explicit virtual-staff view.
export function navigationRole(user) {
  const roles = user?.roles || {};
  if (roles.admin) return 'Admin';
  if (roles.client) return 'Client';
  if (roles.broker) return 'Broker';
  if (roles.sub_broker) return 'Sub-broker';
  if (roles.post_production) return 'Post-production';
  if (roles.contractor) return 'Photographer';
  if (roles.sales_rep) return 'Sales Rep';
  if (roles.virtual_staging) return 'Virtual Services';
  return 'Client';
}
export function canScheduleShoot(user) {
  return ['Admin','Client','Broker','Sub-broker'].includes(navigationRole(user));
}
export function navigationItems(user) {
  const role = navigationRole(user);
  if (role === 'Sales Rep') return [
    {label:'Referrals',path:'/referral',icon:referralIcon},
    {label:'Payouts',path:'/payouts',icon:invoiceIcon},
  ];
  const items = [
    {label:'Home', path:'/dashboard', icon:homeIcon},
    {label:'Photoshoots', path:'/projects_index', icon:projectIcon},
  ];
  if (role === 'Admin') items.push({label:'Photographers', path:'/photographers', icon:photoIcon});
  if (role === 'Photographer') items.push({label:'My Profile',path:`/photographers/${user.id}`,icon:photoIcon});
  if (canScheduleShoot(user)) items.push({label:'Invoices', path:'/invoices_index', icon:invoiceIcon});
  if (['Admin','Photographer'].includes(role)) items.push({label:'Payouts', path:'/payouts', icon:invoiceIcon});
  if (role === 'Admin') items.push({label:'Referrals', path:'/referral', icon:referralIcon});
  return items;
}
const accountItems = [
  {label:'Settings',path:'/settings',icon:settingsIcon},
  {label:'Support',path:'/support',icon:supportIcon},
];
function MenuIcon({close = false}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">{close ? <path d="m6 6 12 12M18 6 6 18"/> : <path d="M4 6h16M4 12h16M4 18h16"/>}</svg>;
}
function LogoutIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 4H5v16h4m5-12 4 4-4 4m-5-4h13"/></svg>;
}
function Account({user, onClick}) {
  const role = navigationRole(user);
  const name = [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(' ');
  const credit = user.account_credit?.amount;
  return <NavLink to="/settings" className="ma-nav-account" title={name} onClick={onClick} aria-label={`Account settings for ${name}`}>
    <UserAvatar user={user}/><span><strong>{name}</strong><small>{role}{credit ? ` · $${credit} credit` : ''}</small></span>
  </NavLink>;
}
function Item({item, onClick, mobile = false}) {
  return <NavLink to={item.path} className={mobile ? 'ma-nav-tab' : 'ma-nav-link'} activeClassName="ma-nav-current" onClick={onClick} aria-label={item.label} title={item.label}>
    <span className="ma-nav-icon" aria-hidden="true" style={{WebkitMaskImage:`url("${item.icon}")`,maskImage:`url("${item.icon}")`}}/><span>{item.label}</span>
  </NavLink>;
}
function AppNavigation({currentUser, location, logout}) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeMenu = () => setClosing(true);
  useEffect(() => {
    if (!closing) return undefined;
    // Keep the drawer mounted until its exit animation has finished.
    const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200;
    const timer = setTimeout(() => {setOpen(false); setClosing(false);}, delay);
    return () => clearTimeout(timer);
  }, [closing]);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const closeButton = useRef(null);
  const items = navigationItems(currentUser);
  const role = navigationRole(currentUser);
  const mobilePaths = role === 'Sales Rep' ? ['/referral','/payouts'] : ['/dashboard','/projects_index',role === 'Photographer' ? '/payouts' : '/invoices_index'];
  const primary = items.filter(item => mobilePaths.includes(item.path));
  const moreActive = [...items.filter(item => !primary.includes(item)), ...accountItems].some(item => location.pathname.startsWith(item.path));
  useEffect(() => {setOpen(false);}, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    function keyboard(event) {
      if (event.key === 'Escape') {event.preventDefault(); closeMenu(); return;}
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('a[href],button:not([disabled])')];
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus();}
    }
    document.addEventListener('keydown',keyboard);
    function resize() {if (window.innerWidth > 750) setOpen(false);}
    window.addEventListener('resize',resize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown',keyboard);
      window.removeEventListener('resize',resize);
      trigger.current?.focus();
    };
  }, [open]);
  const schedule = canScheduleShoot(currentUser) && <NavLink to="/create-project" className="ma-nav-book" onClick={closeMenu}><span aria-hidden="true">+</span><span>Schedule Shoot</span></NavLink>;
  return <>
    <nav className="ma-app-nav" aria-label="Main navigation">
      <NavLink to={navigationRole(currentUser) === 'Sales Rep' ? '/referral' : '/dashboard'} className="ma-nav-brand" aria-label="Modern Angles home"><img src={logo} alt="Modern Angles"/></NavLink>
      <Account user={currentUser}/>
      {schedule}
      <div className="ma-nav-main">{items.map(item => <Item key={item.path} item={item}/>)}</div>
      <div className="ma-nav-footer">{accountItems.map(item => <Item key={item.path} item={item}/>)}<button className="ma-nav-link ma-nav-logout" type="button" onClick={logout}><LogoutIcon/><span>Log Out</span></button></div>
    </nav>
    {createPortal(<><nav className="ma-app-bottom" aria-label="Mobile navigation">{primary.map(item => <Item key={item.path} item={item} mobile/>)}<button ref={trigger} type="button" className={`ma-nav-tab${moreActive || open ? ' ma-nav-current' : ''}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? 'ma-app-menu' : undefined} onClick={() => {setClosing(false); setOpen(true);}}><MenuIcon/><span>More</span></button></nav>
    {open && <div className={`ma-nav-overlay${closing ? ' ma-nav-closing' : ''}`} onClick={event => {if (event.target === event.currentTarget) closeMenu();}}>
      <section ref={dialog} className="ma-nav-drawer" id="ma-app-menu" role="dialog" aria-modal="true" aria-labelledby="ma-app-menu-title">
        <header><h2 id="ma-app-menu-title">Menu</h2><button type="button" ref={closeButton} className="ma-nav-close" aria-label="Close menu" onClick={closeMenu}><MenuIcon close/></button></header>
        <Account user={currentUser} onClick={closeMenu}/>
        {schedule}
        <nav className="ma-nav-main" aria-label="All navigation">{items.map(item => <Item key={item.path} item={item} onClick={closeMenu}/>)}</nav>
        <div className="ma-nav-footer">{accountItems.map(item => <Item key={item.path} item={item} onClick={closeMenu}/>)}<button className="ma-nav-link ma-nav-logout" type="button" onClick={logout}><LogoutIcon/><span>Log Out</span></button></div>
      </section>
    </div>}</>, document.body)}
  </>;
}
export default withRouter(AppNavigation);
